<script setup>
import { computed, onMounted, ref } from 'vue';

import { useStore } from '@/store';
import { initSurvey } from '@/utils/crazyEgg';
import { useListenForGaEvents } from '@base/hooks';
import { TemplateSetupForm, TemplateSetupEditSkeleton } from '@flow/components/template-setup';
import { useTemplateError } from '@flow/hooks';
import { FLAG } from '@base/constants';
import { ChatContainer } from '@chat/shared';

useListenForGaEvents(document.body, 'automation-templates-app');

const store = useStore();

const loading = ref(true);
const chatOpened = ref(false);

const template = computed(() => store.getters['templateSetup/getTemplate']);
const { errorDescription } = useTemplateError(template);

useListenForGaEvents(document.body, 'automation-templates-app');

const handleToggleChat = () => {
  chatOpened.value = !chatOpened.value;
};

onMounted(async () => {
  initSurvey('2807debc-bee2-4e7b-8cec-6ddb3b8b1551');

  loading.value = true;
  await store.dispatch('templateSetup/fetchTemplate');
  loading.value = false;
});
</script>

<template>
  <div :class="$style.container">
    <header :class="$style.header">
      <h1 :class="$style.title">
        {{ $t('template.setup.title') }}
      </h1>

      <!--
        FLOWS_ASSISTANT_CHAT
        TODO: remove FeatureFlag component and keep default
      -->
      <FeatureFlag name="flowsAssistantChat">
        <XButton
          model="alternative"
          design="outlined"
          @click="handleToggleChat"
        >
          <XIcon
            icon="click-ai"
            icon-style="fak"
            size="xl"
            left
          />
          {{ $t('general.clickAi') }}
        </XButton>
        <template #off>
          <XLink
            icon="question-circle"
            :to="$t('links.help.flows.templates.create')"
            data-ga-event="link-help-how-create-template-in-click"
          >
            {{ $t('base.help') }}
          </XLink>
        </template>
      </FeatureFlag>
    </header>

    <p :class="$style.subtitle">
      {{ $t('template.setup.infoEdit') }}
    </p>

    <!-- TOGGLE_TEMPLATE_INSPECT_ENABLED -->
    <!-- Remove the FeatureFlag component and keep slot default  -->
    <FeatureFlag :name="FLAG.TEMPLATE_INSPECT">
      <XAlert
        v-if="errorDescription"
        full-width
        type="error"
      >
        {{ errorDescription }}
      </XAlert>
    </FeatureFlag>

    <XCard>
      <TemplateSetupEditSkeleton v-if="loading" />
      <TemplateSetupForm
        v-else
        is-editing
      />
    </XCard>

    <ChatContainer
      v-if="chatOpened"
      :prompts="[
        $t('chatMessage.helpPrompt.template.whatIs'),
        $t('chatMessage.helpPrompt.template.howToCreate'),
      ]"
      @close="chatOpened = false"
    />
  </div>
</template>

<style lang="scss" module>
.container {
  margin: var(--space-large);
}

.formWrap {
  max-width: 472px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .title {
    margin-bottom: 0;
    color: var(--color-brand-primary-600);
  }
}

.subtitle {
  margin-bottom: var(--space-medium);
  color: var(--color-neutral-800);
}
</style>
